import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { track } from '@/lib/analytics';
import { inAppBrowser } from '@/lib/in-app-browser';
import { errorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/form';
import { AuthLayout, FormError, FormNotice, OrDivider } from './AuthLayout';
import { GoogleButton, GoogleInBrowser } from './GoogleButton';

export default function Login() {
  const [params] = useSearchParams();
  const inApp = inAppBrowser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const passthrough = new URLSearchParams(params);
  passthrough.delete('confirmed');
  passthrough.delete('reset');
  const signupHref = passthrough.toString() ? `/signup?${passthrough}` : '/signup';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setUnconfirmed(false);
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      e.currentTarget.querySelector<HTMLInputElement>(!email.trim() ? '#email' : '#password')?.focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter a valid email address.');
      e.currentTarget.querySelector<HTMLInputElement>('#email')?.focus();
      return;
    }
    setBusy(true);
    track('login_attempt', { method: 'email' });
    try {
      await api.auth.signInWithPassword(email, password);
      track('login', { method: 'email' });
      // GuestOnly redirects as soon as the session lands.
    } catch (err) {
      track('login_failed', {
        method: 'email',
        reason:
          (err as { code?: string }).code === 'email_not_confirmed' ? 'email_unconfirmed' : 'request_failed',
      });
      setError(errorMessage(err));
      setUnconfirmed((err as { code?: string }).code === 'email_not_confirmed');
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.auth.resendConfirmation(email);
      track('email_verification_resent', { source: 'login', method: 'email' });
      toast.success(`Confirmation email sent to ${email}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout title="Sign in to CallSync" subtitle="Welcome back — your team’s calls are waiting.">
      <div className="space-y-4">
        {params.get('confirmed') && <FormNotice>Your email is confirmed. Sign in to continue.</FormNotice>}
        {params.get('reset') && <FormNotice>Your password was updated. Sign in with the new one.</FormNotice>}
      </div>

      {/* In Facebook / Instagram browsers Google sign-in is blocked, so email comes first and Google moves below. */}
      {!inApp && (
        <div className={params.get('confirmed') || params.get('reset') ? 'mt-5' : ''}>
          <GoogleButton text="signin_with" intent="login" />
          <OrDivider />
        </div>
      )}

      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <FormError>
            {error}
            {unconfirmed && (
              <Button
                variant="link"
                size="sm"
                className="mt-1 block text-danger"
                onClick={resend}
                loading={resending}
              >
                Resend confirmation email
              </Button>
            )}
          </FormError>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            disabled={busy}
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-body font-medium text-accent-fg hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              disabled={busy}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute top-1/2 right-1.5 -translate-y-1/2 rounded-md p-1.5 text-fg-subtle hover:text-fg"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </div>

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Sign in
        </Button>
      </form>
      {inApp && (
        <>
          <OrDivider />
          <GoogleInBrowser intent="login" />
        </>
      )}

      <p className="mt-6 text-center text-sm text-fg-muted">
        New to CallSync?{' '}
        <Link to={signupHref} className="font-medium text-accent-fg hover:underline">
          Start a free trial
        </Link>
      </p>
    </AuthLayout>
  );
}
